import styled from '@emotion/styled';
import { Asset } from 'contentful';
import Icon from '../../common-components/component-library/Icon';
import Typography from '../../common-components/component-library/text/Typography';
import Image from '../../common-components/contentful-elements/Image';
import { spacing } from '../../utils/styleguide';
import PostCardVertical from './PostCardVertical';

export type PostCardElement = {
  title: string;
  preamble: string;
  body?: string;
  slug?: string;
  image: Asset;
  readTime?: number;
  placement?: string;
  createdAt: string;
};

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  height: 252px;
  position: relative;
`;

const StyledImage = styled(Image)`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  min-height: 100%;
  min-width: 100%;
  transform: translate(-50%, -50%);
`;

const PreambleWrapper = styled.div`
  margin-top: ${spacing[2]}px;
  margin-bottom: 0;
  display: -webkit-box;
  -webkit-line-clamp: 8;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-height: 390px;
  display: -webkit-box;
  -webkit-line-clamp: 14;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FooterWrapper = styled.footer`
  position: relative;
  margin-top: ${spacing[2]}px;
  display: flex;
  justify-content: space-between;
`;

const FlexBox = styled.div`
  display: flex;
`;

const StyledTable = styled.div`
  display: table;
  overflow: hidden;
`;

const StyledTableCell = styled.div`
  display: table-cell;
  vertical-align: middle;
`;

const IconWrapper = styled.div`
  height: ${spacing[4]}px;
  width: ${spacing[4]}px;
`;

const ReadTimeWrapper = styled.div`
  margin-left: ${spacing[1]}px;
`;

const VerticallyAlignText = ({ content }: { content: JSX.Element }) => {
  return (
    <StyledTable>
      <StyledTableCell>{content}</StyledTableCell>
    </StyledTable>
  );
};

const PostCard = ({ title, preamble, slug, image, readTime, createdAt }: PostCardElement) => {
  const Image = () =>
    image && (
      <ImageWrapper>
        <StyledImage asset={image} />
      </ImageWrapper>
    );
  const Content = () => (
    <ContentWrapper>
      <Typography variant={'h4'}>{title}</Typography>
      <PreambleWrapper>
        <Typography variant="inputField">{preamble}</Typography>
      </PreambleWrapper>
    </ContentWrapper>
  );
  const Footer = () => (
    <FooterWrapper>
      <VerticallyAlignText
        content={
          <div>
            <Typography variant="bodySmallBold">Published</Typography>
            <Typography variant="bodySmall">{createdAt}</Typography>
          </div>
        }
      />
      {readTime !== undefined && (
        <FlexBox>
          <VerticallyAlignText
            content={
              <IconWrapper>
                <Icon name="clock" size={spacing[4]} />
              </IconWrapper>
            }
          />
          <VerticallyAlignText
            content={
              <ReadTimeWrapper>
                <Typography variant="bodySmall">{readTime} min</Typography>
              </ReadTimeWrapper>
            }
          />
        </FlexBox>
      )}
    </FooterWrapper>
  );

  return <PostCardVertical Image={image && Image} Content={Content} slug={slug} Footer={Footer} />;
};

export default PostCard;
